import { useCallback, useMemo } from 'react';

import { type RequestPolicy } from 'urql';

import { NotificationCategoryType } from '@sg/shared-features-notifications';

import { useIsLoggedIn } from '../../context/AuthMachine';
import { useGetUserNotificationPreferencesQuery } from './graphql/GetUserNotificationPreferences.generated';

// ─────────────────────────────────────────────────────────────────────

export const useNotificationPreferencesState = (params?: Params) => {
  const { requestPolicy, pause } = params ?? {};

  const isLoggedIn = useIsLoggedIn();

  const [{ fetching, stale, error, data }, refetch] = useGetUserNotificationPreferencesQuery({
    requestPolicy,
    pause: pause || !isLoggedIn,
  });

  // ─────────────────────────────────────────────────────────────────────

  const resolvedData = data?.getUserNotificationPreferences;
  const hasValidPreferences = resolvedData?.__typename === 'UserNotificationPreferences'; // prettier-ignore

  // ─── Helpers ─────────────────────────────────────────────────────────

  const isOrderPushEnabled = useMemo(() => {
    if (!hasValidPreferences) return false;

    const orderStatusPreference = resolvedData.notificationCategories.find(
      (category) => category.id === NotificationCategoryType.OrderStatus,
    );

    return orderStatusPreference?.pushSubscribe ?? false;
  }, [hasValidPreferences, resolvedData]);

  const isMarketingPushEnabled = useMemo(() => {
    if (!hasValidPreferences) return false;

    const marketingPreference = resolvedData.notificationCategories.find(
      (category) => category.id === NotificationCategoryType.Marketing,
    );

    return marketingPreference?.pushSubscribe ?? false;
  }, [hasValidPreferences, resolvedData]);

  const unresolvedCategoriesNames = useMemo(() => {
    if (!resolvedData || stale) return;

    if (resolvedData.__typename !== 'UserNotificationPreferences') return;

    const unresolvedCategories = resolvedData.notificationCategories.filter(
      (category) => !category.hasResolved,
    );

    if (unresolvedCategories.length === 0) return;

    return unresolvedCategories.map((category) => category.displayName).join(', ');
  }, [resolvedData, stale]);

  // NOTE: An additional wrapper is used to ensure that no extra arguments
  //       are passed to the refetch function.
  const refetchPreferences = useCallback(() => {
    refetch({ requestPolicy: 'cache-and-network' });
  }, [refetch]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    loading: fetching || stale,
    error,
    refetch: refetchPreferences,
    isOrderPushEnabled,
    isMarketingPushEnabled,
    unresolvedCategoriesNames,
    preferences: hasValidPreferences ? resolvedData.notificationCategories : null,
  };
};

// ─── Types ───────────────────────────────────────────────────────────

type Params = {
  requestPolicy?: RequestPolicy;
  pause?: boolean;
};
